// src/utils/printPrescription.js
//
// Abre uma janela com o conteúdo da prescrição/exame e dispara window.print().
// Se `letterheadImageUrl` for fornecida (dataURL recomendado, pra sobreviver à
// nova janela), a imagem é colocada como background da página A4, e o conteúdo
// é renderizado sem o cabeçalho/título padrão (pra não conflitar com o timbre).

import { useSnackbar } from "@/composables/useSnackbar";
import { sanitizePrescriptionHtml } from "./prescriptionSanitize";

const TITLES = {
  medication: "Prescrição de Medicamentos",
  exam: "Solicitação de Exames",
  supplement: "Prescrição de Suplementos",
};

const TOTAL_LABELS = {
  medication: "medicamentos prescritos",
  exam: "exames solicitados",
  supplement: "suplementos prescritos",
};

const ACCENT = {
  medication: "#3E8E73",
  exam: "#4caf50",
  supplement: "#fb8c00",
};

// Valida a área de conteúdo (% da folha A4). Retorna null se inválida/ausente.
function normalizeArea(area) {
  if (!area || typeof area !== "object") return null;
  const n = (v) => (Number.isFinite(+v) ? +v : null);
  const top = n(area.top);
  const left = n(area.left);
  const width = n(area.width);
  const height = n(area.height);
  if (top == null || left == null || width == null || height == null) return null;
  if (width <= 0 || height <= 0) return null;
  return { top, left, width, height };
}

export function printPrescription({
  content,
  prescriptionType,
  selectedItemsLength,
  letterheadImageUrl = null,
  contentArea = null,
}) {
  if (!content || content.trim() === "") {
    throw new Error("Conteúdo vazio");
  }

  // O conteúdo é escrito com document.write numa janela nova, que EXECUTA o que
  // receber. Passa pelo mesmo whitelist do PDF: mantém a formatação, descarta
  // atributos e tags capazes de rodar script ou buscar recurso externo.
  const safeContent = sanitizePrescriptionHtml(content);

  const printWindow = window.open("", "_blank", "width=900,height=1200");
  if (!printWindow) {
    useSnackbar().warning("Por favor, permita pop-ups para imprimir.");
    return;
  }

  const title = TITLES[prescriptionType] || "Prescrição";
  const totalLabel = TOTAL_LABELS[prescriptionType] || "itens";
  const accent = ACCENT[prescriptionType] || "#3E8E73";
  const hasLetterhead = !!letterheadImageUrl;

  // Quando o timbre tem uma área de conteúdo salva (em % da folha A4), o texto
  // é posicionado exatamente dentro dela. Sem área salva, cai no fallback de
  // margens generosas pra não invadir cabeçalho/rodapé do timbre.
  const area = hasLetterhead ? normalizeArea(contentArea) : null;
  const sheetPadding = area
    ? "0"
    : hasLetterhead
      ? "55mm 18mm 40mm 18mm"
      : "18mm 15mm";

  const headerHtml = hasLetterhead
    ? "" // o cabeçalho vem do timbre; não duplica
    : `<h1>${title}</h1>`;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        @page {
          size: A4;
          margin: 0;
        }
        html, body {
          margin: 0;
          padding: 0;
          background: #ffffff;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          line-height: 1.6;
          color: #2c3e50;
        }

        .sheet {
          position: relative;
          width: 210mm;
          min-height: 297mm;
          margin: 0 auto;
          box-sizing: border-box;
          padding: ${sheetPadding};
          ${
            hasLetterhead
              ? `background-image: url('${letterheadImageUrl}');
                 background-size: 100% 100%;
                 background-position: top left;
                 background-repeat: no-repeat;`
              : ``
          }
        }

        /* Área de conteúdo posicionada conforme o retângulo salvo no timbre */
        .content-area {
          position: absolute;
          box-sizing: border-box;
          ${
            area
              ? `top: ${area.top}%;
                 left: ${area.left}%;
                 width: ${area.width}%;
                 height: ${area.height}%;
                 overflow: hidden;`
              : ``
          }
        }

        h1 {
          color: ${accent};
          border-bottom: 3px solid ${accent};
          padding-bottom: 8px;
          margin: 0 0 20px;
          font-size: 22px;
          font-weight: 700;
        }

        .medication-line {
          display: block;
          background-color: #E6EFEA;
          padding: 10px 14px;
          border-radius: 4px;
          margin: 6px 0;
          border-left: 4px solid #3E8E73;
          font-weight: 500;
          page-break-inside: avoid;
        }
        .exam-line {
          display: block;
          background-color: #e8f5e9;
          padding: 10px 14px;
          border-radius: 4px;
          margin: 6px 0;
          border-left: 4px solid #4caf50;
          font-weight: 500;
          page-break-inside: avoid;
        }
        .supplement-line {
          display: block;
          background-color: #fff3e0;
          padding: 10px 14px;
          border-radius: 4px;
          margin: 6px 0;
          border-left: 4px solid #fb8c00;
          font-weight: 500;
          page-break-inside: avoid;
        }
        /* O botão "x" embutido pelo editor não deve aparecer na impressão */
        .medication-line button,
        .exam-line button,
        .supplement-line button { display: none !important; }

        strong, b { font-weight: 700; }
        em, i { font-style: italic; }
        u { text-decoration: underline; }
        ul, ol { padding-left: 28px; margin: 12px 0; }
        li { margin: 6px 0; }

        .footer {
          margin-top: 40px;
          padding-top: 12px;
          border-top: 1px solid #cfd8dc;
          text-align: center;
          color: #607d8b;
          font-size: 11px;
        }

        @media print {
          .sheet {
            box-shadow: none;
          }
          /* Garante que cores de fundo dos blocos saiam na impressão */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      </style>
    </head>
    <body>
      <div class="sheet">
        <div class="${area ? "content-area" : ""}">
          ${headerHtml}
          <div class="content">${safeContent}</div>
          <div class="footer">
            <p>Documento gerado em ${new Date().toLocaleDateString(
              "pt-BR",
            )} às ${new Date().toLocaleTimeString("pt-BR")}</p>
            ${
              selectedItemsLength != null
                ? `<p>Total de ${totalLabel}: ${selectedItemsLength}</p>`
                : ""
            }
          </div>
        </div>
      </div>
    </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.onload = function () {
    printWindow.focus();
    printWindow.print();
  };
}
